import { useEffect, useRef } from 'react'

/**
 * Ambient canvas behind the landing hero: a lead bird flaps across on load,
 * then respawns as faint background drift with a few smaller companions.
 * Purely decorative – aria-hidden, pointer-events none, and skipped entirely
 * under prefers-reduced-motion. Color reads the muted-foreground token each
 * frame so theme switches repaint naturally.
 */
export default function HeroBirds({ className }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const ctx = canvas.getContext('2d')
    const dpr = window.devicePixelRatio || 1
    let W = 900
    let H = 420
    let raf = 0
    const birds = []

    const tokenColor = () =>
      getComputedStyle(document.documentElement).getPropertyValue('--muted-foreground').trim() ||
      '#a39d8c'

    function resize() {
      const r = canvas.parentElement.getBoundingClientRect()
      W = r.width
      H = r.height
      canvas.width = r.width * dpr
      canvas.height = r.height * dpr
      canvas.style.width = `${r.width}px`
      canvas.style.height = `${r.height}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    function spawn(fromLeft, hero) {
      const sz = hero ? 30 : 15 + Math.random() * 15
      return {
        x: fromLeft ? -sz * 2.5 : Math.random() * W,
        y0: hero ? 62 : 26 + Math.random() * (H * 0.45),
        sz,
        s: hero ? 2.6 : 0.16 + sz * 0.012,
        freq: hero ? 4.4 : 3.4 + Math.random() * 1.4,
        ph: Math.random() * Math.PI * 2,
        ph2: Math.random() * Math.PI * 2,
        a: hero ? 0 : 0.07 + Math.random() * 0.05,
        aMax: hero ? 0.5 : 0,
        bob: hero ? 16 : 5,
        hero: !!hero,
      }
    }

    function drawBird(b, t) {
      const flap = Math.sin(t * b.freq + b.ph) + 0.16 * Math.sin(t * b.freq * 2 + b.ph2)
      const sz = b.sz
      const tip = (flap * 0.5 + 0.08) * sz
      ctx.save()
      ctx.translate(b.x, b.y0 + Math.sin(t * 0.5 + b.ph) * b.bob)
      ctx.rotate(flap * 0.035)
      ctx.fillStyle = tokenColor()
      ctx.globalAlpha = b.a
      ctx.beginPath()
      ctx.moveTo(-sz, -tip)
      ctx.quadraticCurveTo(-sz * 0.4, tip * 0.4 + sz * 0.13, 0, sz * 0.06)
      ctx.quadraticCurveTo(sz * 0.4, tip * 0.4 + sz * 0.13, sz, -tip)
      ctx.quadraticCurveTo(sz * 0.44, tip * 0.4 - sz * 0.02, 0, -sz * 0.07)
      ctx.quadraticCurveTo(-sz * 0.44, tip * 0.4 - sz * 0.02, -sz, -tip)
      ctx.closePath()
      ctx.fill()
      ctx.restore()
    }

    resize()
    birds.push(spawn(true, true)) // the lead bird, entering from the left
    const ambient = Math.min(12, Math.max(6, Math.round(W / 140)))
    for (let i = 0; i < ambient; i++) birds.push(spawn(false, false))

    const start = performance.now()
    function tick(now) {
      const t = (now - start) / 1000
      ctx.clearRect(0, 0, W, H)
      birds.forEach((b, idx) => {
        b.x += b.s
        if (b.hero && b.a < b.aMax) b.a = Math.min(b.aMax, b.a + 0.02) // fade the lead bird in
        if (b.x > W + b.sz * 2.5) birds[idx] = spawn(true, false) // after it exits, respawn as ambient
        drawBird(birds[idx], t)
      })
      ctx.globalAlpha = 1
      raf = requestAnimationFrame(tick)
    }

    window.addEventListener('resize', resize)
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className={`pointer-events-none absolute inset-0 ${className || ''}`}
      aria-hidden="true"
    />
  )
}
